import { cva } from "class-variance-authority";

import { cn } from "../../../lib/utils";
import { ComponentProps, ReactNode } from "react";
import { FigmaTypography } from "./figma-typography";

const radioCardVariants = cva(
  "flex items-center gap-2 rounded-lg bg-white p-3 text-left outline-none transition-colors ring-1 ring-inset disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      selected: {
        true: "ring-innoveraZinc-900",
        false: "ring-innoveraZinc-150"
      }
    },
    defaultVariants: {
      selected: false
    }
  }
);

export type TRadioCardProps = ComponentProps<"button"> & {
  selected: boolean;
  icon: ReactNode;
  title: ReactNode;
  description?: ReactNode;
};

export function RadioCard({ selected, icon, title, description, className, type, ...props }: TRadioCardProps) {
  return (
    <button
      type={type || "button"}
      role="radio"
      aria-checked={selected}
      data-slot="radio-card"
      data-state={selected ? "checked" : "unchecked"}
      className={cn(radioCardVariants({ selected }), className)}
      {...props}
    >
      <span
        className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-lg p-2 [&_svg]:size-4",
          selected ? "bg-innoveraZinc-900 text-white shadow-200" : "bg-innoveraZinc-100 text-innoveraZinc-900"
        )}
      >
        {icon}
      </span>
      <span className="flex flex-1 flex-col items-start gap-1 pt-[5px]">
        <span className="flex h-[10px] items-center">
          <FigmaTypography component="span" variant="label-2" color="zinc900">
            {title}
          </FigmaTypography>
        </span>
        {description && (
          <FigmaTypography component="span" variant="body-3" color="zinc500">
            {description}
          </FigmaTypography>
        )}
      </span>
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-xl border border-innoveraZinc-150 p-1.5">
        {selected && <span className="h-2 w-2 shrink-0 rounded-full bg-innoveraZinc-900" />}
      </span>
    </button>
  );
}
